// ?edit=1 on Steps 2 and 3: the provider, and <Editable>, which outlines a value, marks it
// when written over and opens it in the pane.
import type { ReactNode } from "react";

import { type Edit, EditContext, type Mark, markOf, sameTarget, type Target, useEdit } from "./editContext";
import styles from "./amazonOverview.module.css";

export const EditProvider = ({ value, children }: { value: Edit; children: ReactNode }) => (
  <EditContext.Provider value={value}>{children}</EditContext.Provider>
);

const MARK_CLASS: Record<Mark, string> = {
  none: "",
  set: styles.editableSet,
  drift: styles.editableDrift,
};

/** What the mark says, for whoever cannot see its colour. */
const MARK_TITLE: Record<Mark, string> = {
  none: "Open in the edit pane",
  set: "Written over by hand. Open in the edit pane",
  drift: "Written over by hand, and the platform has changed since. Open in the edit pane",
};

/** Outside edit mode, its children as they are. */
export const Editable = ({
  target,
  keys,
  block = false,
  untitled = false,
  children,
}: {
  target: Target;
  /** The overrides the target holds, for its mark. */
  keys: string[];
  block?: boolean;
  /** Sits inside a hover card that already says what it is: no browser tooltip. */
  untitled?: boolean;
  children: ReactNode;
}) => {
  const edit = useEdit();
  if (!edit.editing) return <>{children}</>;
  const mark = "projectId" in target ? markOf(edit, target.projectId, keys) : "none";
  const Tag = block ? "div" : "span";
  return (
    <Tag
      role="button"
      tabIndex={0}
      aria-pressed={sameTarget(edit.selected, target)}
      title={untitled ? undefined : MARK_TITLE[mark]}
      className={[
        styles.editable,
        MARK_CLASS[mark],
        sameTarget(edit.selected, target) ? styles.editableSelected : "",
      ].join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        edit.select(target);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        edit.select(target);
      }}
    >
      {children}
    </Tag>
  );
};
